import { memo, useCallback, useState } from 'react';
import { Badge, Switch, Text, Tooltip } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';
import { isDateWithinLastWeek } from 'utils/dates';
import { capitalize } from 'utils/strings';
import { enableAutomation } from 'services/AutomationsService';
import { BaseReactCell } from '@Sage/ssm-web5';

const EnabledCell = memo(({ data, open, openConfirmModal, reload, value, ...props }) => {
    const [enabled, setEnabled] = useState(data.enabled);

    const performToggle = useCallback(
        (newEnabledValue) => {
            setEnabled(newEnabledValue);
            enableAutomation(data.id, newEnabledValue)
                .then(() => {
                    reload();
                })
                .catch((e) => {
                    console.error(e);
                    errorToast({ text: getLiteral('error_generalerror') });
                    setEnabled(enabled);
                });
        },
        [data?.id, enabled, reload],
    );

    const onChange = useCallback(() => {
        const newEnabledValue = !enabled;

        if (newEnabledValue && openConfirmModal) {
            openConfirmModal({
                name: data.name,
                onConfirm: () => {
                    performToggle(true);
                },
            });
        } else {
            performToggle(newEnabledValue);
        }
    }, [data?.name, enabled, openConfirmModal, performToggle]);

    const onClick = useCallback(() => open(data.id), [data.id, open]);

    const showBadge = isDateWithinLastWeek(data.updateTime) && !data.blocked;

    return (
        <BaseReactCell data={data} {...props}>
            <div className="fm-automation-settings__enabled-cell">
                <div className="fm-automation-settings__enabled-cell__name">
                    {data.blocked && (
                        <Tooltip content={getLiteral('label_automation_stopped_desc_1')}>
                            <div>
                                <Switch
                                    className="fm-automation-settings__enabled-cell__switch"
                                    size="small"
                                    checked={enabled}
                                />
                            </div>
                        </Tooltip>
                    )}
                    {!data.blocked && <Switch size="small" checked={enabled} onChange={onChange} />}
                    <Text
                        className={classnames('fm-automation-settings__enabled-cell__text', {
                            'fm-automation-settings__enabled-cell__text--blocked': data.blocked,
                        })}
                        type="subtitle"
                        medium
                        isTruncated
                        useTooltip
                        onClick={data.blocked ? undefined : onClick}
                    >
                        {value}
                    </Text>
                </div>
                {showBadge && (
                    <Badge type="semanticPositive">{capitalize(getLiteral('label_updated'))}</Badge>
                )}
                {data.blocked && (
                    <Tooltip content={getLiteral('label_automation_stopped_desc_1')}>
                        <Badge type="semanticNegative">
                            {getLiteral('label_automation_stopped')}
                        </Badge>
                    </Tooltip>
                )}
            </div>
        </BaseReactCell>
    );
});

export default EnabledCell;
